import { formatDate } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormArray, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Color, ScaleType } from '@swimlane/ngx-charts';
import { PageTitleService } from 'app/services/page-title/page-title.service';
import { utils, WorkBook, WorkSheet, write } from 'xlsx';
import { BackButtonService } from '../../back-button.service';
import { CodaltComponent } from '../../codalt.component';
import { News } from '../../models/news.interface';
import { SurveyAnswer } from '../../models/surveyanswer.interface';
import { ShortNumberPipe } from '../../pipes/short-number.pipe';
import { ConfirmDialogService } from '../../services/confirm-dialog-service/confirm-dialog.service';
import { NewsService } from '../../services/news.service';
import { SurveyService } from '../../services/survey.service';
import { Settings } from '../../settings.class';

@Component({
    selector: 'app-survey-answers',
    templateUrl: './survey-answers.component.html',
    styleUrls: ['./survey-answers.component.scss'],
    standalone: false,
})
export class SurveyAnswersComponent extends CodaltComponent implements OnInit {
    saving = false;
    typeObj: News;
    faQuestions = new FormArray([], Validators.required);
    fc = {
        questions: this.faQuestions,
    };
    form = new FormGroup(this.fc);
    answers: SurveyAnswer[];
    answersPerQuestion: Map<string, {}>;

    questionGraphs: QuestionGraph[];

    colorScheme: Color = {
        selectable: false,
        name: '',
        group: ScaleType.Ordinal,
        domain: ['#0F0064', '#85D1E4', '#262d33', '#8199ab', '#f2f4f6'],
    };

    colorSchemeSingle: Color = {
        selectable: false,
        name: '',
        group: ScaleType.Linear,
        domain: ['#85D1E4'],
    };

    view: any[] = [700, 300];

    constructor(
        private router: Router,
        private backButtonService: BackButtonService,
        private newsService: NewsService,
        private surveyService: SurveyService,
        public shortNumberPipe: ShortNumberPipe,
        private confirmDialogService: ConfirmDialogService,
        private pageTitleService: PageTitleService
    ) {
        super();
    }

    navigateBack(): void {
        this.backButtonService.navigateBack();
    }

    ngOnInit(): void {
        this.pageTitleService.setTitle('Vragenlijst antwoorden');
        this.watchRouteParams(this.RouteParamnames.newsId, ({ newsId }) => {
            this.subscriptions.add(
                this.newsService.get(String(newsId)).subscribe(typeObj => {
                    this.typeObj = typeObj.data;

                    if (!this.typeObj.survey || this.typeObj.survey?.questions?.length === 0) {
                        this.router.navigate(
                            [this.Routenames.news, this.typeObj.slug, this.Routenames.survey, this.Routenames.edit],
                            { replaceUrl: true }
                        );
                    } else {
                        this.subscriptions.add(
                            this.surveyService.getAnswers(this.typeObj.survey.id).subscribe(answers => {
                                this.answers = answers;
                                this.answersPerQuestion = new Map();
                                this.typeObj.survey.questions.forEach(question => {
                                    question.answers = {};
                                    if (question.type === 'open') {
                                        question.answersUsers = [];
                                    } else {
                                        question.answersUsers = {};
                                    }

                                    this.answers.forEach(surveyAnswer => {
                                        const givenAnswer = surveyAnswer.answers[question.hash];
                                        if (givenAnswer) {
                                            if (Array.isArray(question.answersUsers)) {
                                                question.answersUsers.push(surveyAnswer.user?.id);
                                            } else if (typeof question.answersUsers === 'object') {
                                                if (!question.answersUsers[givenAnswer]) {
                                                    question.answersUsers[givenAnswer] = [];
                                                }
                                                question.answersUsers[givenAnswer].push(surveyAnswer.user?.id);
                                            }

                                            if (typeof givenAnswer === 'object') {
                                                question.options.forEach(option => {
                                                    question.answers[option] = question.answers[option] || [];
                                                    if (!!givenAnswer[option]) {
                                                        question.answers[option].push(surveyAnswer.user.name);
                                                    }
                                                });
                                            } else {
                                                if (!question.answers[givenAnswer]) {
                                                    question.answers[givenAnswer] = [];
                                                }
                                                if (givenAnswer === 'Ja' && question.type === 'boolean-quantity') {
                                                    const quantity = surveyAnswer.answers[`${question.hash}-quantity`];
                                                    question.answers[givenAnswer].push({
                                                        name: surveyAnswer.user.name,
                                                        quantity,
                                                    });
                                                } else {
                                                    question.answers[givenAnswer].push(surveyAnswer.user.name);
                                                }
                                            }
                                        }
                                    });

                                    this.questionGraphs = this.typeObj.survey.questions.map(question => {
                                        const item = {
                                            question: question.question,
                                        } as QuestionGraph;
                                        let min = 0;
                                        let max = 0;
                                        if (['score'].includes(question.type)) {
                                            item.colorScheme = this.colorSchemeSingle;
                                            item.barData = [];
                                            item.xAxisTicks = [];
                                            if (question.answers) {
                                                for (let i = 1; i <= question.limitCount; i++) {
                                                    item.xAxisTicks.push(i);
                                                    item.barData.push({
                                                        name: `${i}`,
                                                        value: question.answers[i]?.length ?? 0,
                                                    });
                                                    if (max < question.answers[i]?.length) {
                                                        max = question.answers[i]?.length;
                                                    }
                                                }
                                            }
                                        }
                                        if (['boolean-quantity', 'boolean'].includes(question.type)) {
                                            item.pieData = [];
                                            for (const property in question.answers) {
                                                item.pieData.push({
                                                    name: property,
                                                    value: question.answers[property]?.length,
                                                });
                                                if (max < question.answers[property]?.length) {
                                                    max = question.answers[property]?.length;
                                                }
                                            }
                                        }
                                        if (['options'].includes(question.type)) {
                                            item.barData = [];
                                            if (question.answers) {
                                                question.options.forEach(option => {
                                                    item.barData.push({
                                                        name: option,
                                                        value: question.answers[option]?.length ?? 0,
                                                    });
                                                    if (max < question.answers[option]?.length) {
                                                        max = question.answers[option]?.length;
                                                    }
                                                });
                                            }
                                        }

                                        item.yAxisTicks = [min];
                                        let first = Math.round(max / 4);
                                        let last = Math.round((max / 4) * 3);
                                        let middle = Math.round(max / 2);
                                        if (!item.yAxisTicks.includes(first)) {
                                            item.yAxisTicks.push(first);
                                        }
                                        if (!item.yAxisTicks.includes(middle)) {
                                            item.yAxisTicks.push(middle);
                                        }
                                        if (!item.yAxisTicks.includes(last)) {
                                            item.yAxisTicks.push(last);
                                        }
                                        item.yAxisTicks.push(max);
                                        return item;
                                    });
                                });
                            })
                        );
                    }
                })
            );
        });
    }

    delete() {
        this.confirmDialogService
            .confirm(
                'Verwijderen',
                `Weet je zeker dat je deze vragenlijst inclusief antwoorden wilt verwijderen?`,
                'Verwijderen',
                'Behouden'
            )
            .then(() =>
                this.surveyService
                    .delete(this.typeObj.survey.id)
                    .subscribe(() => this.router.navigate([this.Routenames.news, this.typeObj.slug]))
            )
            .catch(() => {});
    }

    saveAsExcel() {
        const sheetArray = [];
        this.answers.forEach(surveyAnswer => {
            const answerRow = {};
            answerRow['Naam'] = surveyAnswer.user?.name;
            answerRow['E-mailadres'] = surveyAnswer.user?.email;

            this.typeObj.survey.questions.forEach(question => {
                const givenAnswer = surveyAnswer.answers[question.hash];
                answerRow[question.question] = givenAnswer ?? '-';
            });
            sheetArray.push(answerRow);
        });

        const worksheet: WorkSheet = utils.json_to_sheet(sheetArray);
        worksheet['!cols'] = [{ width: 30 }];

        const workbook: WorkBook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
        const excelBuffer: any = write(workbook, { bookType: 'xlsx', type: 'array' });
        const typeTitle = `${this.typeObj?.title}`.replace(/\s/g, '-').replace(/[^a-z0-9\-]/gi, '');
        const filename = `Antwoorden_${typeTitle}_${formatDate(new Date(), 'yyyy-MMM-d_H-mm', 'nl')}`;
        Settings.saveAsExcelFile(excelBuffer, filename.replace('.', ''));
    }
}

export interface QuestionGraph {
    question: string;
    yAxisTicks: number[];
    xAxisTicks: number[];
    colorScheme: Color;
    areaData?: {
        name: string;
        series: {
            value: number;
            name: string;
        }[];
    }[];
    barData?: {
        name: string;
        value: number;
    }[];
    pieData?: {
        name: string;
        value: number;
    }[];
}

export interface Answer {
    name: string;
    value: string;
}
